import type { BoardChat } from '../../interfaces/BoardChat';
import type { BoardDetails } from '../../interfaces/BoardDetails';
import { normalizeBoardColumns } from '../../utils/boardColumns';
import type { Idea } from '../../interfaces/Idea';
import type { Participant } from '../../interfaces/Participant';
import { publishBoardSocketEvent } from '../../services/boardSocket';
import useUserStore from '../userStore';
import type { ParticipantDeletionState } from './types';

export const mapBoardParticipants = (boardDetails: BoardDetails | null): Participant[] =>
  boardDetails
    ? boardDetails.participants.map((participant) => ({
        id: participant._id,
        name: participant.name,
        votes: 0,
      }))
    : [];

export const findMatchingParticipant = (
  participants: BoardDetails['participants'],
  user: Participant | null
) => {
  if (!user) {
    return null;
  }

  const userId = user.id.trim();
  const userName = user.name.trim().toLowerCase();

  return (
    participants.find((participant) => participant._id === userId) ??
    participants.find((participant) => participant.name.trim().toLowerCase() === userName) ??
    null
  );
};

export const countVotesForOwner = (ideas: Idea[], ownerId: string) =>
  ideas.reduce(
    (total, currentIdea) =>
      total + currentIdea.votes.filter((vote) => vote.ownerId === ownerId).length,
    0
  );

export const countVotesForOwners = (ideas: Idea[], ownerIds: Iterable<string>) => {
  const ownerIdSet = new Set(
    Array.from(ownerIds, (ownerId) => ownerId.trim()).filter((ownerId) => ownerId.length > 0)
  );

  if (ownerIdSet.size === 0) {
    return 0;
  }

  return ideas.reduce(
    (total, currentIdea) =>
      total + currentIdea.votes.filter((vote) => ownerIdSet.has(vote.ownerId)).length,
    0
  );
};

export const getEquivalentParticipantIds = (participants: Participant[], participantId: string) => {
  const normalizedParticipantId = participantId.trim();
  if (!normalizedParticipantId) {
    return [];
  }

  const exactParticipant =
    participants.find((participant) => participant.id === normalizedParticipantId) ?? null;

  if (!exactParticipant) {
    return [normalizedParticipantId];
  }

  const participantNameKey = getParticipantNameKey(exactParticipant.name);

  return participants
    .filter((participant) => getParticipantNameKey(participant.name) === participantNameKey)
    .map((participant) => participant.id);
};

const getDefaultVoteColors = (voteIconType: string) => {
  if (voteIconType === 'thumb') {
    return {
      myVoteColor: '#d4edff',
      theirVoteColor: '#129bf7',
    };
  }

  if (voteIconType === 'star') {
    return {
      myVoteColor: '#ffffd1',
      theirVoteColor: '#fefe00',
    };
  }

  if (voteIconType === 'dot') {
    return {
      myVoteColor: '#75ce1c',
      theirVoteColor: '#529610',
    };
  }

  return {
    myVoteColor: '#ffa2a2',
    theirVoteColor: '#ff0000',
  };
};

export const sanitizeBoardDetails = (boardDetails: BoardDetails): BoardDetails => {
  const normalizedVoteIconType =
    boardDetails.voteIconType === 'thumb' ||
    boardDetails.voteIconType === 'star' ||
    boardDetails.voteIconType === 'dot'
      ? boardDetails.voteIconType
      : 'heart';
  const defaultVoteColors = getDefaultVoteColors(normalizedVoteIconType);

  return {
    ...boardDetails,
    layout: boardDetails.layout ?? 'lanes',
    columns:
      boardDetails.layout === 'columns'
        ? normalizeBoardColumns(boardDetails.columns)
        : boardDetails.columns ?? [],
    cardSorting: boardDetails.cardSorting === 'automatic' ? 'automatic' : 'custom',
    votingStyle:
      boardDetails.votingStyle === 'liking' || boardDetails.votingStyle === 'none'
        ? boardDetails.votingStyle
        : 'dotvoting',
    privateVotingEnabled: boardDetails.privateVotingEnabled ?? false,
    locked: boardDetails.locked ?? false,
    votingEnabled: boardDetails.votingEnabled ?? true,
    votesPerUser: Math.max(0, boardDetails.votesPerUser ?? 0),
    voteIconType: normalizedVoteIconType,
    myVoteColor: boardDetails.myVoteColor ?? defaultVoteColors.myVoteColor,
    theirVoteColor: boardDetails.theirVoteColor ?? defaultVoteColors.theirVoteColor,
    randomizeCardPosition: boardDetails.randomizeCardPosition ?? false,
    giphyEnabled: boardDetails.giphyEnabled ?? true,
    cardsRequireApproval: boardDetails.cardsRequireApproval ?? false,
    viewParticipantNames: boardDetails.viewParticipantNames ?? false,
    allViewParticipantNames: boardDetails.allViewParticipantNames ?? false,
    showVoterNames: boardDetails.showVoterNames ?? false,
    commentsEnabled: boardDetails.commentsEnabled ?? true,
    timerStartedAt: boardDetails.timerStartedAt ?? null,
    timerStoppedAt: boardDetails.timerStoppedAt ?? null,
    timerDurationSeconds:
      typeof boardDetails.timerDurationSeconds === 'number'
        ? boardDetails.timerDurationSeconds
        : null,
    timerStatus:
      boardDetails.timerStatus === 'running' || boardDetails.timerStatus === 'stopped'
        ? boardDetails.timerStatus
        : 'idle',
    timerCompletionActions: boardDetails.timerCompletionActions ?? null,
    timerCompletionActionsAppliedAt: boardDetails.timerCompletionActionsAppliedAt ?? null,
    participantsEnabled: boardDetails.participantsEnabled ?? true,
    participantUploadsEnabled: boardDetails.participantUploadsEnabled ?? true,
    logoVisible: boardDetails.logoVisible ?? true,
    attachmentOptional: boardDetails.attachmentOptional ?? false,
    chatEnabled: boardDetails.chatEnabled ?? true,
    timerEnabled: boardDetails.timerEnabled ?? true,
    background: boardDetails.background
      ? {
          image: boardDetails.background.image ?? '',
          mimetype: boardDetails.background.mimetype ?? '',
          repeat: boardDetails.background.repeat === true,
        }
      : undefined,
    uploadTypes: boardDetails.uploadTypes ?? 'text,image',
  };
};

export const syncViewCard = (ideas: Idea[], viewCard: Idea | null) => {
  if (!viewCard) {
    return null;
  }

  return ideas.find((idea) => idea.id === viewCard.id) ?? null;
};

export const normalizeError = (error: unknown) =>
  error instanceof Error ? error.message : 'An unexpected error occurred';

export const hasBoardAccess = () => {
  const session = useUserStore.getState().getBoardSession();
  return Boolean(session.authToken?.trim() || session.user?.id?.trim());
};

export const hasBoardUser = () => Boolean(useUserStore.getState().getCurrentUser()?.id?.trim());

export const publishBoardChange = (
  boardId: string,
  action: string,
  payload?: Record<string, unknown>
) => {
  publishBoardSocketEvent({
    topic: boardId,
    action,
    ...(payload ?? {}),
  });
};

export const prependOrReplaceIdeas = (currentIdeas: Idea[], incomingIdeas: Idea[]) => {
  const incomingIdeaIds = new Set(incomingIdeas.map((idea) => idea.id));
  const filteredExisting = currentIdeas.filter((idea) => !incomingIdeaIds.has(idea.id));
  return [...incomingIdeas, ...filteredExisting];
};

export const isSameChatMessage = (left: BoardChat, right: BoardChat) =>
  left.ownerName === right.ownerName &&
  left.content === right.content &&
  left.created === right.created;

export const getParticipantNameKey = (name: string) => name.trim().toLowerCase();

export const applyParticipantDeletionToState = (
  state: ParticipantDeletionState,
  removedParticipantIds: Set<string>,
  removedParticipantNameKeys: Set<string>
) => {
  if (removedParticipantIds.size === 0 && removedParticipantNameKeys.size === 0) {
    return state;
  }

  const participants = state.participants.filter((participant) => {
    if (removedParticipantIds.has(participant.id)) {
      return false;
    }

    if (removedParticipantNameKeys.has(getParticipantNameKey(participant.name))) {
      return false;
    }

    return true;
  });

  const ideas = state.ideas.reduce<Idea[]>((accumulator, currentIdea) => {
    if (removedParticipantIds.has(currentIdea.ownerId)) {
      return accumulator;
    }

    const nextVotes = currentIdea.votes.filter((vote) => !removedParticipantIds.has(vote.ownerId));
    const nextComments = currentIdea.comments.filter(
      (comment) => !removedParticipantIds.has(comment.ownerId)
    );

    if (
      nextVotes.length !== currentIdea.votes.length ||
      nextComments.length !== currentIdea.comments.length
    ) {
      accumulator.push({
        ...currentIdea,
        votes: nextVotes,
        comments: nextComments,
      });
      return accumulator;
    }

    accumulator.push(currentIdea);
    return accumulator;
  }, []);

  const chat = state.chat.filter(
    (chatEntry) => !removedParticipantNameKeys.has(getParticipantNameKey(chatEntry.ownerName))
  );

  const nextBoardDetails = state.boardDetails
    ? sanitizeBoardDetails({
        ...state.boardDetails,
        participants: state.boardDetails.participants.filter((participant) => {
          if (removedParticipantIds.has(participant._id)) {
            return false;
          }

          return !removedParticipantNameKeys.has(getParticipantNameKey(participant.name));
        }),
      })
    : state.boardDetails;

  return {
    participants,
    ideas,
    chat,
    boardDetails: nextBoardDetails,
    viewCard: syncViewCard(ideas, state.viewCard),
  };
};

export const isCurrentUserDeletedParticipant = (
  removedParticipantIds: Set<string>,
  removedParticipantNameKeys: Set<string>
) => {
  const currentUser = useUserStore.getState().getCurrentUser();
  if (!currentUser) {
    return false;
  }

  if (removedParticipantIds.has(currentUser.id)) {
    return true;
  }

  return removedParticipantNameKeys.has(getParticipantNameKey(currentUser.name));
};

export const mapSocketParticipant = (rawParticipant: unknown): Participant | null => {
  if (!rawParticipant || typeof rawParticipant !== 'object') {
    return null;
  }

  const participant = rawParticipant as {
    id?: unknown;
    name?: unknown;
  };

  const id = typeof participant.id === 'string' ? participant.id.trim() : '';
  const name = typeof participant.name === 'string' ? participant.name.trim() : '';

  if (!id || !name) {
    return null;
  }

  return { id, name, votes: 0 };
};

export const mapSocketIdea = (rawIdea: unknown, boardId: string): Idea | null => {
  if (!rawIdea || typeof rawIdea !== 'object') {
    return null;
  }

  const idea = rawIdea as Partial<Idea> & {
    type?: unknown;
    processingStatus?: unknown;
  };
  const id = typeof idea.id === 'string' ? idea.id : null;
  const ownerId = typeof idea.ownerId === 'string' ? idea.ownerId : null;
  const type =
    idea.type === 'text' ||
    idea.type === 'url' ||
    idea.type === 'video' ||
    idea.type === 'image' ||
    idea.type === 'file'
      ? idea.type
      : null;
  const processingStatus =
    idea.processingStatus === 'processed' ||
    idea.processingStatus === 'processing' ||
    idea.processingStatus === 'errored'
      ? idea.processingStatus
      : null;
  const suggested = typeof idea.suggested === 'string' ? idea.suggested : null;

  if (!id || !ownerId || !type || !processingStatus || !suggested) {
    return null;
  }

  return {
    id,
    topic: typeof idea.topic === 'string' ? idea.topic : boardId,
    ownerId,
    ownerName: typeof idea.ownerName === 'string' ? idea.ownerName : undefined,
    title: typeof idea.title === 'string' ? idea.title : '',
    content: typeof idea.content === 'string' ? idea.content : '',
    thumbnail: typeof idea.thumbnail === 'string' ? idea.thumbnail : '',
    type,
    approved: idea.approved === true,
    mimeType: typeof idea.mimeType === 'string' ? idea.mimeType : '',
    votes: Array.isArray(idea.votes) ? idea.votes : [],
    pinned: idea.pinned === true,
    sortNo: typeof idea.sortNo === 'number' ? idea.sortNo : undefined,
    columnId: typeof idea.columnId === 'string' ? idea.columnId : undefined,
    processingStatus,
    suggested,
    comments: Array.isArray(idea.comments) ? idea.comments : [],
  };
};
